import React, { createContext, ReactNode, useCallback } from "react";
import { OmniboxSearchResult } from "common-client/generated/graphql";
import {
  searchResultCallbackProps,
  SearchResultProps,
} from "common-client/utils/search";
import { useRecentSearches } from "./hooks/useRecentSearches";
import {
  INITIAL_SEARCH_QUERY_STATE,
  SearchQueryState,
  useSearchQueryState,
} from "./hooks/useSearchQueryState";
import {
  INITIAL_SEARCH_RESULTS_STATE,
  SearchResultsState,
  useSearchResultsState,
} from "./hooks/useSearchResultsState";

type SearchContextType = {
  accountId?: string;
  enableRecentSearches?: boolean;
  calculateBounds?: boolean;
  searchQueryState: ReturnType<typeof useSearchQueryState>[0];
  searchQueryStateDispatch: ReturnType<typeof useSearchQueryState>[1];
  searchResultsState: ReturnType<typeof useSearchResultsState>[0];
  searchResultsDispatch: ReturnType<typeof useSearchResultsState>[1];
  getRecentSearches: ReturnType<typeof useRecentSearches>["getRecentSearches"];
  addRecentSearch: ReturnType<typeof useRecentSearches>["addRecentSearch"];
  removeRecentSearch: ReturnType<
    typeof useRecentSearches
  >["removeRecentSearch"];
  handleSearchResultClick: (searchResult: OmniboxSearchResult) => void;
  unwrappedHandleSearchResultClick: (searchResult: SearchResultProps) => void;
  handleSearchClose: () => void;
  resetSearch: () => void;
};

const DEFAULT_SEARCH_CONTEXT_VALUE: SearchContextType = {
  enableRecentSearches: false,
  calculateBounds: false,
  searchQueryState: INITIAL_SEARCH_QUERY_STATE,
  searchQueryStateDispatch: () => {},
  searchResultsState: INITIAL_SEARCH_RESULTS_STATE,
  searchResultsDispatch: () => {},
  getRecentSearches: () => [],
  addRecentSearch: () => {},
  removeRecentSearch: () => {},
  handleSearchResultClick: () => {},
  unwrappedHandleSearchResultClick: () => {},
  handleSearchClose: () => {},
  resetSearch: () => {},
};

export const SearchContext = createContext<SearchContextType>(
  DEFAULT_SEARCH_CONTEXT_VALUE,
);

export const SearchContextProvider = ({
  children,
  handleSearchResultClick,
  handleSearchClose,
  accountId,
  initialQueryState,
  initialResultsState,
  enableRecentSearches = false,
  calculateBounds = false,
}: {
  handleSearchResultClick: (searchResult: SearchResultProps) => void;
  handleSearchClose?: () => void;
  accountId?: string;
  initialQueryState?: SearchQueryState;
  initialResultsState?: SearchResultsState;
  enableRecentSearches?: boolean;
  calculateBounds?: boolean;
  children: ReactNode;
}) => {
  const [searchQueryState, searchQueryStateDispatch] =
    useSearchQueryState(initialQueryState);
  const [searchResultsState, searchResultsDispatch] =
    useSearchResultsState(initialResultsState);
  const { getRecentSearches, addRecentSearch, removeRecentSearch } =
    useRecentSearches();

  const resetSearch = useCallback(() => {
    searchQueryStateDispatch({ type: "resetSearch" });
    searchResultsDispatch({ type: "resetSearch" });
  }, [searchQueryStateDispatch, searchResultsDispatch]);

  const wrappedHandleSearchResultClick = useCallback(
    (searchResult: OmniboxSearchResult) => {
      if (enableRecentSearches) {
        addRecentSearch(searchQueryState.query ?? "", searchResult);
      }
      handleSearchResultClick(searchResultCallbackProps(searchResult));
      resetSearch();
    },
    [
      handleSearchResultClick,
      resetSearch,
      addRecentSearch,
      enableRecentSearches,
      searchQueryState.query,
    ],
  );

  const wrappedHandleSearchClose = useCallback(() => {
    handleSearchClose?.();
    resetSearch();
  }, [handleSearchClose, resetSearch]);

  return (
    <SearchContext.Provider
      value={{
        accountId,
        enableRecentSearches,
        calculateBounds,
        searchQueryState,
        searchQueryStateDispatch,
        searchResultsState,
        searchResultsDispatch,
        getRecentSearches,
        addRecentSearch,
        removeRecentSearch,
        handleSearchResultClick: wrappedHandleSearchResultClick,
        handleSearchClose: wrappedHandleSearchClose,
        unwrappedHandleSearchResultClick: handleSearchResultClick,
        resetSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
